import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

import { cardPaths } from '@/types/swagger/model'

type paths = cardPaths.paths

type Path = keyof paths
type PathMethod<T extends Path> = keyof paths[T]
export type RequestParams<P extends Path, M extends PathMethod<P>> = paths[P][M] extends {
  requestBody: {
    content: { [x: string]: any }
  }
}
  ? paths[P][M]['requestBody']['content']['application/json;charset=UTF-8']
  : unknown
export type ResponseType<P extends Path, M extends PathMethod<P>> = paths[P][M] extends {
  responses: { 200: { content: { [x: string]: any } } }
}
  ? paths[P][M]['responses'][200]['content']['application/json;charset=UTF-8'] &
      paths[P][M]['responses'][200]['content']['*/*']
  : unknown

export interface ApiServiceParams {
  baseURL: string
  interceptorsRequestConfig: (
    value: InternalAxiosRequestConfig<any>,
  ) => InternalAxiosRequestConfig<any> | Promise<InternalAxiosRequestConfig<any>>
  interceptorsRequestRejected: (error: AxiosError) => any
  interceptorsResponseOutFilled: (response: AxiosResponse<any, any>) => AxiosResponse<any, any>
  interceptorsResponseRejected: (error: AxiosError) => any
}

export interface SendApiParams {
  method: 'get' | 'post' | 'put' | 'delete' | 'patch'
  url: string
  body: object
  params: object
  headers: object
}
class ApiService {
  private api
  private API_TIMEOUT = 10000

  constructor({
    baseURL,
    interceptorsRequestConfig,
    interceptorsRequestRejected,
    interceptorsResponseOutFilled,
    interceptorsResponseRejected,
  }: ApiServiceParams) {
    const api = axios.create({
      timeout: this.API_TIMEOUT,
      withCredentials: true,
      baseURL,
    })

    api.interceptors.request.use(interceptorsRequestConfig, interceptorsRequestRejected)
    api.interceptors.response.use(interceptorsResponseOutFilled, interceptorsResponseRejected)

    this.api = api
  }

  public get<P extends Path, M extends PathMethod<P>>(
    url: P,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ResponseType<P, M>, any>> {
    return this.api.get(url, config)
  }
  public post<P extends Path, M extends PathMethod<P>>(
    url: P,
    data?: RequestParams<P, M> extends undefined ? undefined : Omit<RequestParams<P, M>, 'header'>,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ResponseType<P, M>, any>> {
    return this.api.post(url, data, config)
  }
  public put<P extends Path, M extends PathMethod<P>>(
    url: P,
    data?: RequestParams<P, M> extends undefined ? undefined : Omit<RequestParams<P, M>, 'header'>,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ResponseType<P, M>, any>> {
    return this.api.put(url, data, config)
  }
  public delete<P extends Path, M extends PathMethod<P>>(
    url: P,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ResponseType<P, M>, any>> {
    return this.api.delete(url, config)
  }
  public patch<T>(url: Path, data?: T, config?: AxiosRequestConfig) {
    return this.api.patch(url, data, config)
  }

  public async sendApi<P extends Path, M extends PathMethod<P>>(params: {
    url: P
    method: M
    header?: any
    request?: RequestParams<P, M> extends undefined ? undefined : Omit<RequestParams<P, M>, 'header'>
  }): Promise<AxiosResponse<ResponseType<P, M>, any>> {
    const { method } = params
    let url: string = params.url
    const request: any = params.request
    const isRequest = params.request !== undefined

    const isRequestBody = isRequest && request.body !== undefined
    const isRequestPath = isRequest && request.path !== undefined
    const isRequestQuery = isRequest && request.query !== undefined

    const headers = {
      ...params.header,
    }

    if (isRequestPath) {
      for (const [key, value] of Object.entries(request.path)) {
        url = url.replace(`{${key}}`, `${value}`)
      }
    }

    if (isRequestBody) {
      const [[, requestBody]] = Object.entries(request.body)

      request.body = requestBody
    }

    const response = await this.api({
      method: method as any,
      url: url,
      data: isRequestBody ? { ...request.body } : {},
      params: isRequestQuery ? { ...request.query } : {},
      headers,
    })

    return response
  }
}

export default ApiService
